/**
 * Browser-only, dependency-free viewer shared by React and server-rendered pages.
 * Keep the factory self-contained: public pages embed its generated source in
 * their existing scripts. The companion web-app's generator updates that source.
 * Only explicit photo controls call open; no image interception.
 * Photos and downloaded blobs live in memory only. The caller owns source lifetimes.
 */
export function createPhotoViewer(language = 'en', downloadFile, holdKeyboard) {
  const es = language.startsWith('es');
  const words = es
    ? {
        title: 'Visor de fotos',
        open: 'Ampliar foto',
        close: 'Cerrar',
        previous: 'Anterior',
        next: 'Siguiente',
        in: 'Acercar',
        out: 'Alejar',
        fit: 'Ajustar',
        download: 'Descargar',
        loading: 'Cargando foto…',
        failure: 'No se pudo cargar la foto. Inténtelo de nuevo o vuelva a abrirla desde la página.',
        retry: 'Reintentar',
        saving: 'Preparando descarga…',
        failedSave: 'No se pudo descargar. Inténtelo de nuevo.',
        reveal: 'Mostrar foto',
        photo: 'Foto',
        hint: 'Pellizque o pulse dos veces para ampliar. Arrastre para desplazarse. Ctrl + rueda para ampliar.',
        count: 'de',
      }
    : {
        title: 'Photo viewer',
        open: 'Enlarge photo',
        close: 'Close',
        previous: 'Previous',
        next: 'Next',
        in: 'Zoom in',
        out: 'Zoom out',
        fit: 'Fit',
        download: 'Download',
        loading: 'Loading photo…',
        failure: 'The photo could not load. Retry or reopen it from the page.',
        retry: 'Retry',
        saving: 'Preparing download…',
        failedSave: 'Download failed. Please retry.',
        reveal: 'Show photo',
        photo: 'Photo',
        hint: 'Pinch or double-tap to zoom. Drag to pan. Ctrl + wheel to zoom.',
        count: 'of',
      };
  function imageExtension(contentType) {
    const subtype = (contentType || '').toLowerCase().split('/')[1]?.split(';')[0];
    if (subtype === 'jpeg') return 'jpg';
    if (subtype === 'svg+xml') return 'svg';
    return /^[a-z0-9]{1,10}$/.test(subtype || '') ? subtype : 'img';
  }
  function responseFilename(header) {
    if (!header) return null;
    const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
    if (extended?.[1]) {
      try {
        return decodeURIComponent(extended[1].trim());
      } catch {
        // A malformed encoded name can still have a usable plain fallback.
      }
    }
    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
    return (plain?.[2] ?? plain?.[1])?.trim() || null;
  }
  let dispose;
  return {
    words,
    close() {
      dispose?.(false);
    },
    open(items, start = 0, opener = document.activeElement) {
      dispose?.(false);
      if (!items.length) return;
      let active = true,
        index = start,
        generation = 0,
        scale = 1,
        loaded = false,
        request,
        releaseKeyboard;
      const marker = 'photo-' + Math.random().toString(36).slice(2);
      const originalUrl = location.href;
      const previousOverflow = document.body.style.overflow;
      const previousPadding = document.body.style.paddingRight;
      const gap = innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (gap > 0) document.body.style.paddingRight = gap + 'px';
      const dialog = document.createElement('dialog');
      dialog.setAttribute('aria-label', words.title);
      dialog.setAttribute('aria-modal', 'true');
      dialog.dataset.photoViewer = '';
      dialog.style.cssText =
        'position:fixed;inset:0;margin:0;width:100%;height:100%;height:100dvh;max-width:none;max-height:none;border:0;padding:0;background:#101c21;color:white;z-index:2147483647;overflow:hidden;font:1rem system-ui;box-sizing:border-box;';
      const toolbar = document.createElement('div');
      toolbar.style.cssText =
        'display:flex;align-items:center;gap:6px;flex-wrap:wrap;padding:max(8px,env(safe-area-inset-top)) 8px 8px;';
      const button = (label, fn) => {
        const node = document.createElement('button');
        node.type = 'button';
        node.textContent = label;
        node.setAttribute('aria-label', label);
        node.style.cssText =
          'font:inherit;width:auto;min-width:44px;min-height:44px;padding:8px 10px;margin:0;border:1px solid #80989f;border-radius:8px;background:#20353d;color:white;cursor:pointer;';
        node.onclick = fn;
        toolbar.append(node);
        return node;
      };
      const close = (back = true) => {
        if (!active) return;
        active = false;
        dispose = undefined;
        if (photo) {
          photo.onload = null;
          photo.onerror = null;
          photo.removeAttribute('src');
        }
        generation++;
        request?.abort();
        observer.disconnect();
        window.removeEventListener('popstate', onBack);
        window.removeEventListener('pagehide', onLeave);
        dialog.close?.();
        dialog.remove();
        releaseKeyboard?.();
        document.body.style.overflow = previousOverflow;
        document.body.style.paddingRight = previousPadding;
        if (back && history.state?.photoViewer === marker && location.href === originalUrl) history.back();
        else if (history.state?.photoViewer === marker) {
          const state = { ...history.state };
          delete state.photoViewer;
          history.replaceState(state, '');
        }
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      };
      dispose = close;
      const closeButton = button(words.close, () => close());
      const previous = button(words.previous, () => show((index - 1 + items.length) % items.length));
      const next = button(words.next, () => show((index + 1) % items.length));
      const zoomOut = button('−', () => zoom(scale / 1.5));
      zoomOut.setAttribute('aria-label', words.out);
      const zoomIn = button('+', () => zoom(scale * 1.5));
      zoomIn.setAttribute('aria-label', words.in);
      const fit = button(words.fit, () => zoom(1));
      const save = button(words.download, async () => {
        if (save.disabled) return;
        const version = generation;
        save.disabled = true;
        status.textContent = words.saving;
        request?.abort();
        request = new AbortController();
        let blobUrl;
        try {
          const item = items[index];
          const url = new URL(item.src, location.href);
          if (!['http:', 'https:', 'blob:'].includes(url.protocol) || url.origin !== location.origin) throw new Error();
          // Local previews already hold authorized bytes. Fetching their blob
          // URL would require a broader connect-src policy on public forms.
          if (url.protocol === 'blob:') {
            const link = document.createElement('a');
            link.href = item.src;
            link.download = (item.filename || 'photo.' + imageExtension(item.contentType)).replace(
              /[\\/\x00-\x1f]/g,
              '_',
            );
            document.body.append(link);
            link.click();
            link.remove();
            status.textContent = '';
            return;
          }
          const file = downloadFile
            ? await downloadFile(item.src, request.signal)
            : await fetch(item.src, { credentials: 'same-origin', cache: 'no-store', signal: request.signal }).then(
                async (response) => {
                  if (!response.ok) throw new Error();
                  return {
                    blob: await response.blob(),
                    filename: responseFilename(response.headers.get('content-disposition')),
                  };
                },
              );
          if (!active || version !== generation || location.href !== originalUrl) return;
          if (!file.blob.type.startsWith('image/')) throw new Error();
          blobUrl = URL.createObjectURL(file.blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          const extension = imageExtension(file.blob.type);
          link.download = (file.filename || item.filename || 'photo.' + extension).replace(/[\\/\x00-\x1f]/g, '_');
          document.body.append(link);
          link.click();
          link.remove();
          status.textContent = '';
        } catch {
          if (active && version === generation) status.textContent = words.failedSave;
        } finally {
          // A short delay lets the browser consume the explicit download before revocation.
          if (blobUrl) setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          if (active && version === generation) save.disabled = false;
        }
      });
      const caption = document.createElement('div');
      caption.style.cssText = 'padding:0 12px 6px;overflow-wrap:anywhere;';
      caption.setAttribute('aria-live', 'polite');
      const status = document.createElement('div');
      status.setAttribute('role', 'status');
      status.style.cssText = 'padding:0 12px;';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = words.retry;
      retry.style.cssText = closeButton.style.cssText;
      retry.hidden = true;
      retry.onclick = () => show(index);
      const frame = document.createElement('div');
      frame.dataset.photoFrame = '';
      frame.tabIndex = 0;
      frame.setAttribute('aria-label', words.hint);
      frame.style.cssText =
        'position:relative;min-height:0;flex:1;overflow:auto;overscroll-behavior:contain;touch-action:none;';
      const canvas = document.createElement('div');
      canvas.style.cssText = 'display:flex;min-width:100%;min-height:100%;';
      frame.append(canvas);
      const hint = document.createElement('div');
      hint.textContent = words.hint;
      hint.style.cssText = 'font-size:.75rem;padding:8px 12px max(8px,env(safe-area-inset-bottom));';
      const layout = document.createElement('div');
      layout.style.cssText = 'display:flex;flex-direction:column;height:100%;';
      layout.append(toolbar, caption, status, retry, frame, hint);
      dialog.append(layout);
      document.body.append(dialog);
      let photo,
        naturalWidth = 1,
        naturalHeight = 1;
      const fitted = () =>
        Math.max(
          1,
          Math.min(frame.clientWidth - 16, naturalWidth, ((frame.clientHeight - 16) * naturalWidth) / naturalHeight),
        );
      function zoom(value, x = frame.clientWidth / 2, y = frame.clientHeight / 2) {
        const oldWidth = photo?.getBoundingClientRect().width || fitted();
        const oldHeight = (oldWidth * naturalHeight) / naturalWidth;
        const fx = (frame.scrollLeft + x - Math.max(0, (frame.clientWidth - oldWidth) / 2)) / oldWidth;
        const fy = (frame.scrollTop + y - Math.max(0, (frame.clientHeight - oldHeight) / 2)) / oldHeight;
        scale = Math.max(1, Math.min(8, value));
        if (photo) {
          const width = fitted() * scale;
          photo.style.width = width + 'px';
          photo.style.height = (width * naturalHeight) / naturalWidth + 'px';
          frame.scrollLeft = fx * width - x + Math.max(0, (frame.clientWidth - width) / 2);
          frame.scrollTop =
            (fy * width * naturalHeight) / naturalWidth -
            y +
            Math.max(0, (frame.clientHeight - (width * naturalHeight) / naturalWidth) / 2);
        }
        zoomOut.disabled = !loaded || scale === 1;
        zoomIn.disabled = !loaded || scale === 8;
        fit.disabled = !loaded;
      }
      function show(nextIndex) {
        generation++;
        request?.abort();
        index = nextIndex;
        scale = 1;
        loaded = false;
        const version = generation,
          item = items[index];
        photo = null;
        canvas.replaceChildren();
        retry.hidden = true;
        save.disabled = true;
        previous.hidden = next.hidden = items.length < 2;
        caption.textContent = `${index + 1} ${words.count} ${items.length} · ${item.alt || words.photo}`;
        dialog.setAttribute('aria-label', `${words.title}: ${item.alt || words.photo}`);
        zoom(1);
        const load = () => {
          status.textContent = words.loading;
          save.disabled = false;
          photo = document.createElement('img');
          photo.alt = item.alt || words.photo;
          photo.draggable = false;
          photo.style.cssText =
            'display:block;flex:none;margin:auto;max-width:none;max-height:none;object-fit:contain;image-orientation:from-image;user-select:none;';
          const current = photo;
          photo.onload = () => {
            if (!active || generation !== version) return;
            naturalWidth = current.naturalWidth;
            naturalHeight = current.naturalHeight;
            // A viewBox-only diagram has a ratio, not a useful pixel width.
            if (/\.svgz?(?:[?#]|$)/i.test(item.src)) {
              const ratio = naturalWidth / naturalHeight;
              naturalWidth = Math.max(naturalWidth, frame.clientWidth);
              naturalHeight = naturalWidth / ratio;
            }
            loaded = true;
            save.disabled = false;
            status.textContent = '';
            zoom(1);
          };
          photo.onerror = () => {
            if (!active || generation !== version) return;
            status.textContent = words.failure;
            retry.hidden = false;
            current.remove();
          };
          canvas.replaceChildren(photo);
          photo.src = item.src;
        };
        if (item.notice) {
          status.textContent = item.notice;
          const reveal = document.createElement('button');
          reveal.type = 'button';
          reveal.textContent = words.reveal;
          reveal.style.cssText = closeButton.style.cssText + 'margin:auto;';
          reveal.onclick = load;
          canvas.append(reveal);
        } else load();
      }
      // One scroll surface: pointers pan it and two pointers zoom around their midpoint.
      const pointers = new Map();
      let lastTap = 0,
        lastTouch = 0,
        moved = false,
        pinched = false;
      frame.onpointerdown = (event) => {
        if (event.button !== 0 || !loaded) return;
        frame.setPointerCapture?.(event.pointerId);
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 1) {
          moved = false;
          pinched = false;
        }
        if (pointers.size > 1) pinched = true;
      };
      frame.onpointermove = (event) => {
        const point = pointers.get(event.pointerId);
        if (!point) return;
        const before = [...pointers.values()];
        const distance = (values) => Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);
        const dx = event.clientX - point.x,
          dy = event.clientY - point.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 2) {
          const after = [...pointers.values()],
            rect = frame.getBoundingClientRect();
          zoom(
            (scale * distance(after)) / Math.max(1, distance(before)),
            (after[0].x + after[1].x) / 2 - rect.left,
            (after[0].y + after[1].y) / 2 - rect.top,
          );
        } else {
          frame.scrollLeft -= dx;
          frame.scrollTop -= dy;
        }
      };
      frame.onpointerup = (event) => {
        pointers.delete(event.pointerId);
        if (event.pointerType === 'touch') lastTouch = Date.now();
        if (event.pointerType === 'touch' && !moved && !pinched) {
          const now = Date.now();
          if (now - lastTap < 350) {
            zoom(scale === 1 ? 2 : 1);
            lastTap = 0;
          } else lastTap = now;
        }
      };
      frame.onpointercancel = (event) => {
        pointers.delete(event.pointerId);
        lastTap = 0;
      };
      frame.ondblclick = (event) => {
        event.preventDefault();
        if (Date.now() - lastTouch < 500) return;
        if (loaded) zoom(scale === 1 ? 2 : 1);
      };
      frame.addEventListener(
        'wheel',
        (event) => {
          if (!event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          const rect = frame.getBoundingClientRect();
          if (loaded) zoom(scale * Math.exp(-event.deltaY / 200), event.clientX - rect.left, event.clientY - rect.top);
        },
        { passive: false },
      );
      dialog.oncancel = (event) => {
        event.preventDefault();
        close();
      };
      dialog.onkeydown = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close();
        } else if (event.key === 'Tab') {
          const stops = [...dialog.querySelectorAll('button:not(:disabled), [tabindex="0"]')].filter(
            (node) => !node.hidden && !node.parentElement?.hidden,
          );
          const first = stops[0],
            last = stops[stops.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        } else if (scale === 1 && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          show((index + (event.key === 'ArrowRight' ? 1 : items.length - 1)) % items.length);
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          zoom(scale * 1.5);
        } else if (event.key === '-') {
          event.preventDefault();
          zoom(scale / 1.5);
        }
      };
      const observer = new ResizeObserver(() => {
        if (loaded) zoom(scale);
      });
      observer.observe(frame);
      const onBack = () => close(false),
        onLeave = () => close(false);
      window.addEventListener('popstate', onBack);
      window.addEventListener('pagehide', onLeave);
      history.pushState({ ...history.state, photoViewer: marker }, '');
      dialog.showModal();
      releaseKeyboard = holdKeyboard?.();
      closeButton.focus({ preventScroll: true });
      show(index);
      // Backdrop/drag release never closes a photo. Close, Escape and Back do.
    },
  };
}
